/**
 * Owns zoom and the world-space center of the view, with clamping to the map. Pure, so
 * the math is unit-tested; the scene copies the result onto Phaser's camera each frame.
 *
 * Zoom ranges from "the whole map fits" (fit zoom) to MAX_ZOOM_FACTOR × that.
 * The view never shows beyond the map edge; when the map is narrower than the screen in
 * one dimension, it is centered in that dimension.
 */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export const MAX_ZOOM_FACTOR = 3;

export class CameraModel {
  zoom = 1;
  centerX: number;
  centerY: number;

  constructor(
    readonly mapWidth: number,
    readonly mapHeight: number,
  ) {
    this.centerX = mapWidth / 2;
    this.centerY = mapHeight / 2;
  }

  fitZoom(v: Viewport): number {
    return Math.min(v.width / this.mapWidth, v.height / this.mapHeight);
  }

  /** Whole map on screen, centered. The starting view. */
  fit(v: Viewport): void {
    this.zoom = this.fitZoom(v);
    this.centerX = this.mapWidth / 2;
    this.centerY = this.mapHeight / 2;
    this.clamp(v);
  }

  /** Screen-space finger delta → the map follows the finger. */
  panBy(dx: number, dy: number, v: Viewport): void {
    this.centerX -= dx / this.zoom;
    this.centerY -= dy / this.zoom;
    this.clamp(v);
  }

  /** Multiply zoom by `factor`, keeping the world point under screen point (sx, sy) fixed. */
  zoomAt(factor: number, sx: number, sy: number, v: Viewport): void {
    const minZ = this.fitZoom(v);
    const maxZ = minZ * MAX_ZOOM_FACTOR;
    const newZoom = Math.min(maxZ, Math.max(minZ, this.zoom * factor));
    const offX = sx - v.width / 2;
    const offY = sy - v.height / 2;
    const worldX = this.centerX + offX / this.zoom;
    const worldY = this.centerY + offY / this.zoom;
    this.zoom = newZoom;
    this.centerX = worldX - offX / this.zoom;
    this.centerY = worldY - offY / this.zoom;
    this.clamp(v);
  }

  /** Screen point → world point under the current view. */
  toWorld(sx: number, sy: number, v: Viewport): { x: number; y: number } {
    return {
      x: this.centerX + (sx - v.width / 2) / this.zoom,
      y: this.centerY + (sy - v.height / 2) / this.zoom,
    };
  }

  /** Keep the view inside the map (or centered on it when the map is smaller than the view). */
  clamp(v: Viewport): void {
    const minZ = this.fitZoom(v);
    if (this.zoom < minZ) this.zoom = minZ;
    const visW = v.width / this.zoom;
    const visH = v.height / this.zoom;
    this.centerX =
      this.mapWidth <= visW
        ? this.mapWidth / 2
        : Math.min(this.mapWidth - visW / 2, Math.max(visW / 2, this.centerX));
    this.centerY =
      this.mapHeight <= visH
        ? this.mapHeight / 2
        : Math.min(this.mapHeight - visH / 2, Math.max(visH / 2, this.centerY));
  }
}
